<template>
    <div>
        <lti-drawer
            :active="showEbookPreviewDrawer"
            :title="`Ebook: ${selectedEbookTitle}`"
            is-ebook
            :maximized="isDrawerMaximized"
            @close="$emit('close')"
            @update:active="$emit('update:active', $event)"
            @restore="handleRestore"
            @state="handleMaximizedChange"
        >
            <div class="is-flex is-flex-direction-column is-height-full">
                <div ref="ltiContainer" class="iframe-container is-flex-grow-1">
                    <div v-if="loading" class="ebook-loading-overlay">
                        <div class="has-text-centered p-6">
                            <e-icon icon="spinner" spin />
                            <p class="mt-2">Loading Ebook…</p>
                        </div>
                    </div>
                </div>
            </div>
        </lti-drawer>
    </div>
</template>

<script>
import queryString from 'query-string';
import LtiDrawer from '@/components/lti/LtiDrawer.vue';
import UserSetting from '@/models/UserSetting.js';

export default {
    name: 'EbookPreviewDrawer',

    components: {
        LtiDrawer,
    },

    props: {
        ltiResourceLinkId: {
            type: [String, Number],
            required: true,
        },
        selectedEbookTitle: {
            type: String,
            default: '',
        },
        selectedEbookFormat: {
            type: String,
            default: '',
        },
        showEbookPreviewDrawer: {
            type: Boolean,
            default: false,
        },
        hasSpecificLocation: {
            type: Boolean,
            default: false,
        },
        continueReading: {
            type: Boolean,
            default: false,
        },
    },

    emits: ['close', 'update:active'],

    data() {
        return {
            loading: false,
            iframeEl: null,
            iframeLoadHandler: null,
            isDrawerMaximized: false,
        };
    },

    computed: {
        ltiParameters() {
            const params = {
                has_specific_location: this.hasSpecificLocation,
                continue_reading: this.continueReading,
            };

            if (this.selectedEbookFormat) {
                params.format = this.selectedEbookFormat;
            }

            return queryString.stringify(params);
        },

        isVitalSourceIntegrated() {
            return this.$store.state.Integrations.isVitalSourceInIntegrationEnabled;
        },

        currentUser() {
            return this.$store.state.User;
        },

        hasRequiredPermissions() {
            // Check if user has admin or instructor role
            if (!this.currentUser) {
                return false;
            }

            return (
                this.$store.getters['User/hasRole']('Admin') ||
                this.$store.getters['User/hasRole']('Owner') ||
                this.$store.getters['User/hasRole']('Instructor') ||
                this.$store.getters['User/hasRole']('Instruction Admin') ||
                this.$store.getters['User/hasRole']('Student')
            );
        },

        canAccessEbookSelector() {
            return this.isVitalSourceIntegrated && this.hasRequiredPermissions;
        },
    },

    watch: {
        showEbookPreviewDrawer(isActive) {
            if (isActive) {
                this.loadSavedDrawerState();
                this.loadLaunchData();
            } else {
                this.cleanup();
            }
        },

        ltiResourceLinkId(newVal, oldVal) {
            if (newVal !== oldVal && this.showEbookPreviewDrawer) {
                // MUST reload iframe for LTI to pick up accept_multiple
                this.loadLaunchData();
            }
        },

        hasSpecificLocation(newVal, oldVal) {
            if (newVal !== oldVal && this.showEbookPreviewDrawer) {
                this.loadLaunchData();
            }
        },

        continueReading(newVal, oldVal) {
            if (newVal !== oldVal && this.showEbookPreviewDrawer) {
                this.loadLaunchData();
            }
        },

        selectedEbookFormat(newVal, oldVal) {
            if (newVal !== oldVal && this.showEbookPreviewDrawer) {
                this.loadLaunchData();
            }
        },
    },

    mounted() {
        if (this.showEbookPreviewDrawer) {
            this.loadSavedDrawerState();
            this.loadLaunchData();
        }
    },

    beforeUnmount() {
        this.cleanup();
    },

    methods: {
        handleRestore(payload) {
            if (payload?.relaunch === false) {
                return;
            }

            this.loadLaunchData();
        },

        async loadSavedDrawerState() {
            try {
                const setting = UserSetting.findByKey('ebook_drawer_state');
                const savedState = setting?.value;

                if (savedState === 'fullscreen') {
                    this.isDrawerMaximized = true;
                } else if (savedState === 'normal') {
                    this.isDrawerMaximized = false;
                } else {
                    this.isDrawerMaximized = true;
                }
            } catch (error) {
                // On error, default to fullscreen mode
                this.$edverumToast.open({
                    message: 'Failed to load ebook drawer state',
                    type: 'is-warning',
                });
                this.isDrawerMaximized = true;
            }
        },

        async handleMaximizedChange(isMaximized) {
            this.isDrawerMaximized = isMaximized;
            await this.saveDrawerState(isMaximized);
        },

        async saveDrawerState(isMaximized) {
            try {
                const setting = UserSetting.findByKey('ebook_drawer_state');
                const state = isMaximized ? 'fullscreen' : 'normal';
                await setting.set(state);
            } catch (error) {
                // Silently fail - don't disrupt user experience
                this.$edverumToast.open({
                    message: 'Failed to save ebook drawer state',
                    type: 'is-warning',
                });
            }
        },

        cleanup() {
            if (this.iframeEl && this.iframeLoadHandler) {
                this.iframeEl.removeEventListener('load', this.iframeLoadHandler);
                this.iframeLoadHandler = null;
            }
            this.iframeEl = null;
            this.loading = false;
        },

        async loadLaunchData() {
            // Check if VitalSource is integrated and user has required permissions
            if (!this.canAccessEbookSelector) {
                // Redirect to error page
                this.$router.push({ name: 'ebook-access-denied' });
                return;
            }

            if (this.ltiResourceLinkId) {
                this.$nextTick(() => {
                    const container = this.$refs.ltiContainer;
                    const baseUrl = import.meta.env.VITE_APP_URL || window.location.origin;

                    if (container) {
                        const src = `${baseUrl}/lti/links/${this.ltiResourceLinkId}?${this.ltiParameters}`;

                        // If a previous iframe exists, remove its load listener and clear references
                        if (this.iframeEl && this.iframeLoadHandler) {
                            this.iframeEl.removeEventListener('load', this.iframeLoadHandler);
                            this.iframeLoadHandler = null;
                        }
                        this.iframeEl = null;

                        // Recreate iframe to ensure fresh launch
                        container.innerHTML = '';

                        const iframe = document.createElement('iframe');
                        iframe.style.width = '100%';
                        iframe.style.height = '100%';
                        iframe.style.border = 'none';
                        iframe.src = src;

                        // show loading overlay until iframe loads
                        this.loading = true;

                        this.iframeLoadHandler = () => {
                            this.loading = false;
                        };

                        iframe.addEventListener('load', this.iframeLoadHandler);

                        container.appendChild(iframe);

                        this.iframeEl = iframe;
                    }
                });
            }
        },
    },
};
</script>

<style>
.iframe-container {
    position: relative;
}

.ebook-loading-overlay {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(255, 255, 255, 0.85);
    z-index: 10;
}
</style>
